import { AxiosError } from 'axios';
import isEmpty from 'lodash-es/isEmpty';
import { PaymentAuthResult } from '../types/payment';
import useLocalization from './useLocalization';
import { useInvalidateQuery } from './useInvalidateQuery';
import { useExtendedSnackbar } from './useExtendedSnackbar';
import { QUERY_KEYS } from '../constants';
import { API_ROUTES } from '../constants/apiRoutes';
import { useModal } from './useDialog';
import { isFeedApiError } from '../utils/api';

type PaymentResponseWithAuthResults = {
  authResults?: PaymentAuthResult[];
};

const KYC_REQUIRED_ERROR_CODE = 'KYC_REQUIRED';

export const usePaymentResponseHandler = <T extends PaymentResponseWithAuthResults>() => {
  const { t } = useLocalization();
  const { enqueueSnackbar, showErrorMessage } = useExtendedSnackbar();
  const { closeModal } = useModal();
  const invalidateData = useInvalidateQuery();

  const handlePaymentSuccess = (
    response: T,
    options: {
      successMessage: string;
      closeModal?: boolean;
      invalidateKeys?: string[];
    }
  ) => {
    const { successMessage, closeModal: shouldCloseModal = true, invalidateKeys = [QUERY_KEYS.balance] } = options;

    if (response.authResults && !isEmpty(response.authResults)) {
      const { limitName, limitValue } = response.authResults[0];
      showErrorMessage(limitName, { limitValue });
    } else {
      enqueueSnackbar(t(successMessage), { variant: 'success' });
      invalidateData([...new Set([...invalidateKeys, API_ROUTES.WALLET_TRANSACTIONS_HISTORY.PLAYER])]);

      if (shouldCloseModal) {
        closeModal();
      }
    }
  };

  const handlePaymentError = (
    error: Error,
    options: {
      failureMessage: string;
    }
  ) => {
    const { failureMessage } = options;

    if (!(error instanceof AxiosError)) {
      enqueueSnackbar(t('unexpectedError'), { variant: 'error' });
      return;
    }

    const errorResponseObject = error.response?.data;

    if (errorResponseObject && isFeedApiError(errorResponseObject)) {
      // Only a KYC denial can reflect changed profile state; outages and config errors cannot.
      if (errorResponseObject.code === KYC_REQUIRED_ERROR_CODE) {
        invalidateData([QUERY_KEYS.customerData]);
      }

      showErrorMessage(errorResponseObject.code);
      return;
    }

    enqueueSnackbar(t(failureMessage), { variant: 'error' });
  };

  return {
    handlePaymentSuccess,
    handlePaymentError,
  };
};
